import { ref, computed, watch } from 'vue'
import dayjs from '../day.js'
import { useCalendarStore } from '../pinia-stores/calendar.js'
import { useKeycloakStore } from '../pinia-stores/keycloak.js'

async function fetchMyGroups() {
  const { default: axios } = await import('axios')
  const keycloakStore = useKeycloakStore()
  const { data } = await axios({
    url: import.meta.env.VITE_APP_URL + 'groups/my-groups',
    method: 'GET',
    // params: { userId: keycloakStore.subject },
    headers: { Authorization: `Bearer ${keycloakStore.token}` },
    timeout: 15000,
  })
  const content = Array.isArray(data) ? data : (data?.content ?? [])
  return content;
}

/**
 * Resolves the single "most important" calendar event of a group (or, when no
 * group is given, the user's first group) so it can be pinned on top of the
 * feed. Priority: a manually pinned event first, then the nearest upcoming one.
 * The result is a computed so unpinning/re-pinning stays in sync with the store.
 */
export function useImportantEvent(groupId) {
  const calendarStore = useCalendarStore()
  const resolvedGroupId = ref(groupId || null)
  const loading = ref(false)

  const events = computed(() => {
    if (!resolvedGroupId.value) return []
    return (calendarStore.getEvents(resolvedGroupId.value) || []).filter(
      (e) => !e.status || e.status === 'ACTIVE',
    )
  })

  const event = computed(() => {
    const now = dayjs().valueOf()
    const list = events.value.filter((e) => e.startAt)
    const byStart = (a, b) =>
      dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf()

    const pinned = list.filter((e) => e.pinned).sort(byStart)
    if (pinned.length) return pinned[0]

    const upcoming = list
      .filter((e) => dayjs(e.startAt).valueOf() >= now)
      .sort(byStart)
    if (upcoming.length) return upcoming[0]

    return null
  })

  async function load() {
    loading.value = true
    try {
      let targetGroup = groupId || null
      if (!targetGroup) {
        try {
          const groups = await fetchMyGroups()
          targetGroup =
            groups.find((g) => g.groupId)?.groupId || groups[0]?.id || null
        } catch (e) {
          targetGroup = null
        }
      }
      resolvedGroupId.value = targetGroup
      if (targetGroup) {
        const from = dayjs().startOf('month').format('YYYY-MM-DD')
        const to = dayjs().add(2, 'month').endOf('month').format('YYYY-MM-DD')
        await calendarStore.fetchEvents(targetGroup, from, to)
      }
    } finally {
      loading.value = false
    }
  }

  if (groupId) watch(() => groupId, () => load())

  return { event, groupId: resolvedGroupId, loading, load }
}
